import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formatYmdInBrussels, brusselsStartUtcFromYmd } from '@/lib/brusselsSchedule';

export function useUpcomingEvents(limit = 3) {
    return useQuery({
        queryKey: ['events', 'upcoming', limit],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('events')
                .select('*')
                .gte('date', new Date().toISOString())
                .eq('is_published', true)
                .order('date', { ascending: true })
                .limit(limit);

            if (error) {
                console.error('Error fetching events:', error);
                // Return fallback data for development if table is empty/missing
                return getFallbackData().slice(0, limit);
            }
            return data && data.length > 0 ? data : [];
        },
        staleTime: 5 * 60 * 1000,
    });
}

export function useEvents(filters = {}) {
    return useQuery({
        queryKey: ['events', 'list', filters],
        queryFn: async () => {
            let query = supabase
                .from('events')
                .select('*')
                .eq('is_published', true)
                .order('date', { ascending: true });

            if (filters.type && filters.type !== 'Tous') {
                query = query.eq('type', filters.type);
            }

            // Future: add more filters (season, etc.)

            const { data, error } = await query;

            if (error) {
                console.error('Error fetching events:', error);
                // Fallback data
                return getFallbackData().filter(e => filters.type === 'Tous' || !filters.type || e.type === filters.type);
            }
            return data && data.length > 0 ? data : getFallbackData().filter(e => filters.type === 'Tous' || !filters.type || e.type === filters.type);
        },
        staleTime: 5 * 60 * 1000,
    });
}

export function useAdminEvents() {
    return useQuery({
        queryKey: ['events', 'admin-list'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('events')
                .select('*')
                .order('date', { ascending: false });

            if (error) {
                console.error('Error fetching admin events:', error);
                return [];
            }
            return data || [];
        },
    });
}

export function useEventBySlug(slug) {
    return useQuery({
        queryKey: ['events', 'detail', slug],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('events')
                .select('*')
                .eq('slug', slug)
                .single();

            if (error) {
                console.error('Error fetching event:', error);
                // Fallback
                return getFallbackData().find(e => e.slug === slug);
            }
            return data;
        },
        enabled: !!slug,
    });
}

export function useEventById(id) {
    return useQuery({
        queryKey: ['events', 'id', id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('events')
                .select('*')
                .eq('id', id)
                .single();

            if (error) throw error;
            return data;
        },
        enabled: !!id,
    });
}

function fallbackIsoDaysAhead(daysAhead) {
    const d = new Date();
    d.setDate(d.getDate() + daysAhead);
    const ymd = formatYmdInBrussels(d);
    return brusselsStartUtcFromYmd(ymd).toISOString();
}

function getFallbackData() {
    return [
        {
            id: '1',
            title: 'L’Intelligence Artificielle au quotidien',
            slug: 'ia-quotidien',
            type: 'Conférence',
            date: fallbackIsoDaysAhead(5),
            end_time: '16:30:00',
            location: 'Parc de Juliénas 1, 6940 Barvaux',
            description: 'Comprendre les enjeux de l’IA dans notre vie de tous les jours. Une plongée fascinante dans le monde des algorithmes qui régissent notre quotidien.',
            image_url: null,
            speaker_civility: 'Monsieur',
            speaker_name: 'Dr. Jean Dupont',
            speaker_title: 'Expert en IA',
            speaker_description: 'Chercheur associé au CNRS, spécialisé dans l\'éthique des algorithmes et l\'impact social des technologies émergentes.'
        },
        {
            id: '2',
            title: 'Balade en forêt de Wéris',
            slug: 'balade-weris',
            type: 'Excursion',
            date: fallbackIsoDaysAhead(12),
            end_time: '16:30:00',
            location: 'Wéris',
            description: 'Découverte des mégalithes et du patrimoine local. Prévoir de bonnes chaussures de marche.',
            image_url: null
        },
        {
            id: '3',
            title: 'Goûter de Printemps',
            slug: 'gouter-printemps',
            type: 'Goûter',
            date: fallbackIsoDaysAhead(20),
            end_time: '16:30:00',
            location: 'Parc de Juliénas 1, 6940 Barvaux',
            description: 'Moment de convivialité autour de tartes maison. Inscription obligatoire.',
            image_url: null
        }
    ];
}
